import { Mic, Paperclip, Send, Smile } from 'lucide-react';
import React, { useEffect, useState } from 'react';

/**
 * Modern Chat Input Component
 * Based on conversation-mockup.html design
 *
 * @param {Object} props
 * @param {Function} props.onSend - Send message handler
 * @param {Function} props.onFileUpload - File upload handler
 * @param {Function} props.onVoiceInput - Voice input handler
 * @param {string} props.placeholder - Input placeholder text
 * @param {boolean} props.disabled - Disable input
 */
export function ChatInput({
  value = '',
  onChange,
  onSend,
  onFileUpload,
  onVoiceInput,
  placeholder = "Type your message...",
  disabled = false,
  isListening = false,
  isSpeaking = false
}) {
  const [message, setMessage] = useState(value);
  const [isComposing, setIsComposing] = useState(false);

  // Update local state when value prop changes
  useEffect(() => {
    setMessage(value);
  }, [value]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setMessage(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && onSend) {
      onSend(message.trim());
      setMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && onFileUpload) {
      onFileUpload(file);
    }
  };

  const handleVoiceInput = () => {
    if (onVoiceInput) {
      onVoiceInput();
    }
  };

  return (
    <div className="border-t border-gray-200 p-4">
      <form onSubmit={handleSubmit} className="flex gap-2 items-center">
        {/* File Upload Button */}
        <label className="p-2 hover:bg-gray-100 rounded-lg transition cursor-pointer" title="Attach file">
          <Paperclip className="w-5 h-5 text-gray-600" />
          <input
            type="file"
            className="hidden"
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.txt"
            disabled={disabled}
          />
        </label>

        {/* Message Input */}
        <input
          type="text"
          value={message}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f7911d] disabled:bg-gray-100 disabled:cursor-not-allowed"
        />

               {/* Voice Input Button */}
               <button
                 type="button"
                 onClick={handleVoiceInput}
                 disabled={disabled}
                 className={`p-2 rounded-lg transition disabled:opacity-50 ${
                   isListening
                     ? 'bg-red-100 text-red-600 hover:bg-red-200'
                     : isSpeaking
                     ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                     : 'hover:bg-gray-100'
                 }`}
                 title={isListening ? "Listening..." : isSpeaking ? "Speaking..." : "Voice input"}
               >
                 <Mic className={`w-5 h-5 ${isListening ? 'animate-pulse' : ''}`} />
               </button>

        {/* Emoji Button */}
        <button
          type="button"
          disabled={disabled}
          className="p-2 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
          title="Emoji"
        >
          <Smile className="w-5 h-5 text-gray-600" />
        </button>

        {/* Send Button */}
        <button
          type="submit"
          disabled={!message.trim() || disabled}
          className="px-4 py-2 bg-[#f7911d] text-white rounded-lg hover:bg-[#e67e0a] transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className="w-4 h-4" />
          <span>Send</span>
        </button>
      </form>
    </div>
  );
}

export default ChatInput;

